import React from 'react';
import {
  seasonTitle,
  seasonHeadline,
  seasonOthersLine,
  seasonScopeLine,
  industryBreakdown,
  SEASON_DIRECTIONS,
  SEASON_NOTE,
} from '../shared/guidanceSeason';
import { GUIDANCE_STYLE } from '../shared/callHighlightStyles';
import { seasonSegments, countsToSegments } from '../utils/guidanceSeasonCard';

/**
 * Desktop rendering of guidance by earnings season (issue #387) for the Market Analysis
 * "Guidance" card. `season` is the one `pickSeason` chose from the same payload the mobile Brief
 * card is built on, and every word and count comes from `src/shared/guidanceSeason.js` — this
 * component only draws.
 *
 * Collapsed: the split as one bar, its legend, and the one-line takeaway.
 * Expanded: the same, then the split per industry. Deliberately NO per-company list: with a
 * season's worth of calls it becomes a wall within weeks, and the industry rows carry the read.
 */

// One stacked bar of segments, reused for the season-level bar and each industry's own bar below
// (both are built from `countsToSegments`/`seasonSegments`, so a count and its drawn width can
// never drift apart).
const Bar = ({ className, segments }) => (
  <div className={className}>
    {segments.map((s) => s.count > 0 && (
      <span key={s.key} style={{ flex: s.count, background: s.color }} />
    ))}
  </div>
);

const SeasonBar = ({ season }) => {
  const segments = seasonSegments(season);
  return (
    <>
      <Bar className="guidance-season__bar" segments={segments} />
      <div className="guidance-season__legend">
        {segments.map((s) => (
          <span key={s.key}>
            <i style={{ background: s.color }} />
            {s.count} {s.label}
          </span>
        ))}
      </div>
    </>
  );
};

// The bar plus its one-line takeaway — the whole collapsed card, and the top of the expanded one.
const SeasonHeader = ({ season }) => (
  <>
    <SeasonBar season={season} />
    <div className="guidance-season__headline">
      {seasonHeadline(season)} · {seasonTitle(season)}
    </div>
  </>
);

export const GuidanceSeasonSummary = ({ season }) => (
  <div className="guidance-season">
    <SeasonHeader season={season} />
  </div>
);

// The same split per industry, one thin bar each. The counts are printed in the legend's
// colours (named just above, in SeasonBar's legend), so a row carries no words of its own —
// which is what keeps six rows from reading as a wall.
const IndustryRows = ({ season }) => {
  const rows = industryBreakdown(season);
  if (!rows.length) return null;
  return (
    <div className="guidance-season__industries">
      <div className="list-movers__heading">By industry</div>
      {rows.map((row) => (
        <div key={row.industry} className="guidance-season__industry" title={`${row.calls} ${row.calls === 1 ? 'call' : 'calls'}`}>
          <span className="guidance-season__industry-name">{row.industry}</span>
          <Bar className="guidance-season__industry-bar" segments={countsToSegments(row.counts)} />
          <span className="guidance-season__industry-counts">
            {SEASON_DIRECTIONS.map((key) => (
              <span key={key} style={{ color: GUIDANCE_STYLE[key].bar }}>{row.counts[key]}</span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
};

export const GuidanceSeasonDetail = ({ season }) => {
  const others = seasonOthersLine(season);
  return (
    <div className="guidance-season">
      <SeasonHeader season={season} />
      <IndustryRows season={season} />
      <div className="list-movers__note">
        {[others, seasonScopeLine(season), SEASON_NOTE].filter(Boolean).join(' ')}
      </div>
    </div>
  );
};
